import React, { useState, useEffect } from 'react';
import styles from './style.module.css';
import { BotiqueLayoutImage, BoutiqueLayoutOverlay } from '../../assets/webp';
import { StoreAIBot } from '../../components/StoreAIBot';
import Typography from '../../components/Typography/Typography';
import { StoreCart, StoreImgName, StoreSearchIconDark } from '../../assets/svgs';
import FlatButtons from '../../components/FlatButton/FlatButton';
import { AddProductModal } from '../../components/AddProductModal/AddProductModal';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import { StorePreviewModal } from '../../components/StorePreviewModal';
import AnimatedButton from '../../components/AnimatedButton';
import { useGetTemplateByIdQuery } from '../../redux/services/storesApi';


interface Product {
    id: string;
    name: string;
    description: string;
    category: string;
    image: string;
    quantity: number;
}

interface BoutiqueInfoProps {
    backgroundImage?: string;
    overlayImage?: string;
}

export const AddStoreProducts: React.FC<BoutiqueInfoProps> = ({
    backgroundImage = BotiqueLayoutImage,
    overlayImage = BoutiqueLayoutOverlay
}) => {
    const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
    const [isAddProductModalOpen, setIsAddProductModalOpen] = useState(false);
    const [products, setProducts] = useState<Product[]>([]);
    const [isMobile, setIsMobile] = useState(false);

    const navigate = useNavigate();
    const { storeId } = useParams<{ storeId: string }>();
    const storeDetails = useSelector((state: RootState) => state.CreateStore);

    const { data: templateData, isLoading, isError } = useGetTemplateByIdQuery(
        storeDetails.storeTemplate || '',
        { skip: !storeDetails.storeTemplate }
    );

    const currentBackgroundImage = templateData?.image_preview_url || backgroundImage;
    const currentOverlayImage = templateData?.model_url || overlayImage;
    const templateName = storeDetails?.name || templateData?.name || 'Store Name';

    useEffect(() => {
        const checkScreenSize = () => {
            setIsMobile(window.innerWidth <= 1024);
        };

        checkScreenSize();
        window.addEventListener('resize', checkScreenSize);
        return () => window.removeEventListener('resize', checkScreenSize);
    }, []);

    useEffect(() => {
        console.log('=== AddStoreProducts Debug ===');
        console.log('Store ID from params:', storeId);
        console.log('Store Details from Redux:', storeDetails);
        console.log('Store Name:', storeDetails.name);
        console.log('Logo URL:', storeDetails.logoFile);
        console.log('Template ID:', storeDetails.storeTemplate);
        console.log('Template Data:', templateData);
    }, [storeId, storeDetails, templateData]);

    const handlePreviewStore = () => {
        if (isMobile) {
            setIsPreviewModalOpen(true);
        }
    };

    const closePreviewModal = () => {
        setIsPreviewModalOpen(false);
    };

    const handleOpenAddProductModal = () => {
        setIsAddProductModalOpen(true);
    };

    const handleCloseAddProductModal = () => {
        setIsAddProductModalOpen(false);
    };

    const handleAddProduct = (product: Product) => {
        setProducts(prevProducts => [...prevProducts, product]);
        setIsAddProductModalOpen(false);
    };

    const reduxProducts = useSelector((state: RootState) => state.CreateProduct);

    const getProductPosition = (position: string, index: number) => {
        const positionMap: { [key: string]: { x: number; y: number } } = {
            'A1': { x: 50, y: 80 },
            'A2': { x: 50, y: 180 },
            'B1': { x: 180, y: 80 },
            'B2': { x: 180, y: 180 },
            'C1': { x: 310, y: 80 },
            'C2': { x: 310, y: 180 },
        };
        return positionMap[position] || { x: 50 + (index * 80), y: 120 };
    };

    const handlePrevious = () => {
        if (storeId) {
            navigate(`/add-info/${storeDetails.storeTemplate}`);
        } else {
            navigate('/choose-template');
        }
    };

    const handleNext = () => {
        if (storeId) {
            navigate(`/customize-store/${storeId}`);
        } else {
            navigate('/customize-store');
        }
    };

    return (
        <div className={styles.boutiqueContainer}>
            <div className={styles.contentWrapper}>
                <div className={styles.header}>
                    <Typography
                        variant='heading5'
                        fontFamily='satoshi'
                        weight='bold'
                        className={styles.title}>
                        Step 3: Add Product to Store
                    </Typography>
                    {isMobile && (
                        <div className={styles.headerActions}>
                            <AnimatedButton
                                variant='secondary'
                                onClick={handlePreviewStore}
                                className={styles.previewStoreBtn}
                            >
                                <img
                                    src={StoreImgName}
                                    alt="Welcome Name Icon"
                                    className={styles.icon}
                                />
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.previewStoreBtnText}
                                >
                                    Preview Store
                                </Typography>
                            </AnimatedButton>
                        </div>
                    )}
                </div>

                <div className={styles.mainContent}>
                    <div className={styles.contentGrid}>
                        <div className={styles.leftPanel}>
                            <div className={styles.section}>
                                <Typography
                                    variant='heading3'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    className={styles.sectionTitle}>
                                    Product Rack
                                </Typography>

                                <div className={styles.formGroup}>
                                    <div className={styles.formLabel}>
                                        <Typography
                                            variant='bodyRegular'
                                            fontFamily='satoshi'
                                            weight='regular'
                                            size='base'
                                            className={styles.labelText}>
                                            Add Products To Your Product Rack
                                        </Typography>
                                    </div>

                                    {reduxProducts.length > 0 ? (
                                        <div className={styles.productGrid}>
                                            {reduxProducts.map((product) => (
                                                <div key={product.store_id} className={styles.productCard}>
                                                    <div className={styles.productImageContainer}>
                                                        <img
                                                            src={product.image_file || ''}
                                                            alt={product.name}
                                                            className={styles.productImage}
                                                        />
                                                    </div>
                                                    <div className={styles.productInfo}>
                                                        <Typography
                                                            variant='bodyRegular'
                                                            fontFamily='satoshi'
                                                            weight='bold'
                                                            className={styles.productName}>
                                                            {product.name}
                                                        </Typography>
                                                        <Typography
                                                            variant='bodySmall'
                                                            fontFamily='satoshi'
                                                            weight='regular'
                                                            className={styles.productQuantity}>
                                                            Quantity: {product.quantity}
                                                        </Typography>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <div className={styles.emptyProductContainer}>
                                            <Typography
                                                variant='bodyRegular'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.emptyText}>
                                                No products added yet
                                            </Typography>
                                        </div>
                                    )}

                                    <div className={styles.addProductContainer}>
                                        <AnimatedButton
                                            variant='secondary'
                                            onClick={handleOpenAddProductModal}
                                            className={styles.addProductBtn}>
                                            <Typography
                                                variant='buttonText'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.addProductBtnText}>
                                                Add Product
                                            </Typography>
                                        </AnimatedButton>
                                    </div>
                                </div>

                                {isMobile && (
                                    <div className={styles.mobileButtons}>
                                        <AnimatedButton
                                            variant='secondary'
                                            onClick={handlePreviewStore}
                                            className={styles.previewDemoBtn}
                                        >
                                            <Typography
                                                variant='buttonText'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.previewDemoBtnText}
                                            >
                                                Preview Demo Store
                                            </Typography>
                                        </AnimatedButton>
                                    </div>
                                )}
                            </div>
                        </div>

                        {!isMobile && (
                            <div className={styles.rightPanel}>
                                <div className={styles.storeContainer}>
                                    <div className={styles.topNav}>
                                        <FlatButtons className={styles.storeName}>
                                            {storeDetails.logoFile ? (
                                                <img
                                                    src={storeDetails.logoFile}
                                                    alt="Store Logo"
                                                    className={styles.storeLogo}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.style.display = 'none';
                                                        console.error('Failed to load logo:', storeDetails.logoFile);
                                                    }}
                                                />
                                            ) : (
                                                <img
                                                    src={StoreImgName}
                                                    alt="Default Store Icon"
                                                    className={styles.icon}
                                                />
                                            )}
                                            <Typography
                                                variant='buttonText'
                                                fontFamily='satoshi'
                                                weight='regular'
                                                size='base'
                                                className={styles.storeNameText}>
                                                {storeDetails.name || templateName}
                                            </Typography>
                                        </FlatButtons>
                                        <FlatButtons className={styles.navIcons}>
                                            <img
                                                src={StoreSearchIconDark}
                                                alt="Search Icon"
                                                className={styles.icon}
                                            />
                                            <img
                                                src={StoreCart}
                                                alt="Cart Icon"
                                                className={styles.icon}
                                            />
                                        </FlatButtons>
                                    </div>

                                    <div className={styles.storeLayout}>
                                        <img
                                            src={currentBackgroundImage}
                                            alt="Store background"
                                            className={styles.backgroundImage}
                                            onError={(e) => {
                                                const target = e.target as HTMLImageElement;
                                                target.src = backgroundImage;
                                            }}
                                        />

                                        {currentOverlayImage && (
                                            <div className={styles.overlay}>
                                                <img
                                                    src={currentOverlayImage}
                                                    alt="Store overlay"
                                                    className={styles.overlayImage}
                                                    onError={(e) => {
                                                        const target = e.target as HTMLImageElement;
                                                        target.src = overlayImage;
                                                    }}
                                                />
                                            </div>
                                        )}
                                        {reduxProducts && reduxProducts.length > 0 && reduxProducts.map((product, index) => {
                                            const position = getProductPosition(product.position_in_store, index);

                                            return (
                                                <div
                                                    key={`store-product-${index}`}
                                                    className={styles.storeProduct}
                                                    style={{
                                                        left: `${position.x}px`,
                                                        top: `${position.y}px`,
                                                    }}
                                                >
                                                    {product.image_file && (
                                                        <img
                                                            src={product.image_file ?? ''}
                                                            alt={product.name}
                                                            className={styles.storeProductImage}
                                                            onError={(e) => {
                                                                const target = e.target as HTMLImageElement;
                                                                target.style.display = 'none';
                                                            }}
                                                        />
                                                    )}
                                                    <Typography
                                                        variant='bodySmall'
                                                        fontFamily='satoshi'
                                                        weight='regular'
                                                        className={styles.storeProductName}>
                                                        {product.name}
                                                    </Typography>
                                                </div>

                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className={styles.actionSection}>
                        <div className={styles.navigationContainer}>
                            <AnimatedButton
                                variant='primary'
                                onClick={handlePrevious}
                                className={styles.previousBtn}
                            >
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.previousBtnText}>
                                    Previous
                                </Typography>
                            </AnimatedButton>

                            <AnimatedButton
                                variant='primary'
                                onClick={handleNext}
                                className={styles.nextBtn}
                            >
                                <Typography
                                    variant='buttonText'
                                    fontFamily='satoshi'
                                    weight='regular'
                                    size='base'
                                    className={styles.nextBtnText}>
                                    Next
                                </Typography>
                            </AnimatedButton>
                        </div>
                        <div className={styles.progressSection}>
                            <div className={styles.progressContainer}>
                                <Typography
                                    variant='bodyLarge'
                                    fontFamily='satoshi'
                                    weight='bold'
                                    className={styles.stepText}>
                                    Step 3 of 4
                                </Typography>
                                <div className={styles.progressBar}>
                                    <div className={styles.progressFilled}></div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {isPreviewModalOpen && (
                <StorePreviewModal
                    isOpen={isPreviewModalOpen}
                    onClose={closePreviewModal}
                    backgroundImage={currentBackgroundImage}
                    overlayImage={currentOverlayImage}
                    storeName={storeDetails.name || templateName}
                    storeLogo={storeDetails.logoFile || undefined}
                    products={reduxProducts}
                />
            )}

            <AddProductModal
                isOpen={isAddProductModalOpen}
                onClose={handleCloseAddProductModal}
                onAddProduct={handleAddProduct}
                productIndex={null}
                storeId={storeId || ''}
            />

            <StoreAIBot />
        </div>
    );
};